import React, { useCallback, useContext, useState } from 'react'
import {
  Button,
  Box,
  TextField,
  MenuItem,
  Typography,
  Switch,
  Menu,
  ListItemText,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  usePopupState,
  bindMenu,
  bindTrigger,
} from 'material-ui-popup-state/hooks'
import useReportDetails from 'hooks/useReportDetails'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import LockResetIcon from '@mui/icons-material/LockReset'
import { ChartOptionElement } from 'gqlTypes'
import useChartOptions from 'hooks/useChartOptions'
import GlobalResetDialog from './GlobalResetDialog'

export type GlobalSettingsMenuProps = {
  reportId: string
}

const GlobalSettingsMenu: React.FC<GlobalSettingsMenuProps> = ({
  reportId,
}) => {
  const [globalResetOpen, setGlobalResetOpen] = useState(false)
  const { cohorts, subgroups, hiddenSubgroupOptions } =
    useReportDetails(reportId)

  const {
    cohort,
    subgroup,
    pastResults,
    config,
    onChangeCohort,
    onChangeSubgroup,
    onChangePastResults,
    onChangeConfig,
    handleGlobalReset,
  } = useChartOptions(reportId, ChartOptionElement.Report)

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'globalSettingsMenu',
  })

  const hidden = config?.hidden && subgroup ? config.hidden[subgroup] || [] : []

  const handleSetHidden = useCallback(
    (value: string[]) => {
      let newHidden = config && config.hidden ? { ...config.hidden } : {}

      newHidden[subgroup || 'overall'] = value

      onChangeConfig({ ...config, hidden: newHidden })
    },
    [onChangeConfig]
  )

  const handleChangeHidden = useCallback(
    (key: string) => {
      handleSetHidden(
        hidden.includes(key)
          ? hidden.filter((e: string) => e !== key)
          : [...hidden, key]
      )
    },
    [handleSetHidden, hidden]
  )

  return (
    <Box display="flex" justifyContent="flex-end" alignItems="flex-end" pb={2}>
      <Box pt={1} pr={2}>
        <Tooltip title="Set all charts to default settings" placement="top">
          <IconButton onClick={() => setGlobalResetOpen(true)}>
            <LockResetIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <GlobalResetDialog
        open={globalResetOpen}
        reportId={reportId}
        onClose={() => setGlobalResetOpen(false)}
        onReset={() => handleGlobalReset(reportId)}
      />
      <Box pt={1} pr={2}>
        <TextField
          select
          label="Default Cohort"
          value={cohort ? cohort : cohorts[0]}
          size="small"
          sx={{ width: '200px' }}
        >
          {cohorts.map((option) => (
            <MenuItem
              key={option}
              value={option}
              onClick={() => onChangeCohort(option)}
            >
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box pt={1} pr={2}>
        <TextField
          select
          label="Default Subgroup"
          value={subgroup ? subgroup : subgroups[0]}
          size="small"
          sx={{ width: '200px' }}
        >
          {subgroups.map((option) => (
            <MenuItem
              key={option}
              value={option}
              onClick={() => onChangeSubgroup(option)}
            >
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box pt={1} pr={2} sx={{ flex: '0 0 180px' }}>
        <Typography
          component="label"
          sx={{
            display: 'block',
            fontWeight: '400',
            fontSize: '0.8rem',
            color: 'rgba(0, 0, 0, 0.6)',
            lineHeight: '1',
            width: '160px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            paddingBottom: 0.5,
          }}
        >
          {subgroup ? subgroup : subgroups[0]}
        </Typography>
        <Button
          startIcon={
            hidden.length === 0 ? (
              <ViewColumnIcon />
            ) : (
              <FilterAltIcon color="secondary" />
            )
          }
          variant="outlined"
          sx={{ whiteSpace: 'nowrap' }}
          {...bindTrigger(popupState)}
        >
          Show/Hide Data
        </Button>
        <Menu {...bindMenu(popupState)}>
          <MenuItem
            onClick={(e: React.MouseEvent) => {
              if (hidden.length === 0) {
                const options: string[] =
                  hiddenSubgroupOptions.filter((s) => s.name === subgroup)[0]
                    ?.values || []
                handleSetHidden(options)
              } else {
                handleSetHidden([])
              }
            }}
          >
            <Checkbox checked={hidden.length === 0} />
            <ListItemText>Select All</ListItemText>
          </MenuItem>
          {hiddenSubgroupOptions
            .filter((s) => s.name === subgroup)[0]
            ?.values.map((v) => (
              <MenuItem onClick={() => handleChangeHidden(v!)} key={v}>
                <Checkbox checked={!hidden.includes(v!)} />
                <ListItemText>{v}</ListItemText>
              </MenuItem>
            ))}
        </Menu>
      </Box>
      <Box sx={{ position: 'relative', top: '2px' }}>
        <Typography
          component="label"
          sx={{
            fontWeight: '400',
            fontSize: '0.8rem',
            color: 'rgba(0, 0, 0, 0.6)',
            lineHeight: '1',
            whiteSpace: 'nowrap',
          }}
        >
          Past Results
        </Typography>
        <Box textAlign="center">
          <Switch
            checked={!!pastResults}
            onChange={(e, val) => onChangePastResults(val)}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default GlobalSettingsMenu
